/**
 * TRAYECTOS — el cable lo mide el plano, no el vendedor (escalón E4, 2026-08-22).
 *
 * Qué se mide aquí y qué no, para no cobrar dos veces:
 *   · Cada SALIDA del catálogo ya trae su cable y su tubo locales —la bajada al
 *     contacto, la subida al apagador, el tramo por el muro—: `SAL-CTO` 9 m / 5 m,
 *     `SAL-ILU` 7 m / 4 m, `SAL-ESC` 14 m / 8 m (`catalogo.js`). Eso NO se vuelve a
 *     contar. (Refutación 10 del plan: las salidas de luz sí bajan al apagador, y
 *     la de escalera dos veces; el catálogo ya lo cobra así.)
 *   · Lo que el plano sí sabe y el catálogo no: cuánto hay del TABLERO a cada
 *     recinto por plafón — la alimentación. Se estima con distancia ortogonal
 *     (el cable va por muros y plafón, no en diagonal) entre el tablero y el
 *     centroide del recinto, más un entrepiso si están en plantas distintas.
 *
 * Todo valor que no viene de una medición va marcado: si el tablero no está en
 * el plano se supone una distancia por defecto y la partida lo dice en voz alta
 * (`metrosSupuestos`), como ya hace el motor eléctrico con sus distancias.
 *
 * Alturas y holguras son PROVISIONALES (`sinVerificar`): práctica de obra
 * residencial, pendientes de fijar con fuente (NOM-001-SEDE no fija alturas de
 * salida; se cargan con `dato-normativo` cuando Juan decida el estándar de la
 * casa). Se declaran para que la explicación al cliente sea honesta.
 */

import { distOrtogonal } from './geometria.js'

export const ALTURAS_BASE = {
  sinVerificar: true,
  /** Altura libre entre piso y plafón, m. */
  entrepiso: 2.5,
  /** Altura del contacto sobre el piso, m. */
  contacto: 0.4,
  /** Altura del apagador sobre el piso, m. */
  apagador: 1.2,
  /** Holgura de tendido sobre el trayecto medido (curvas, remates, empalmes). */
  holgura: 0.10,
  /** Distancia supuesta del tablero al recinto cuando el tablero no está en el plano, m. */
  trayectoPorDefecto: 8,
  /** Conductores por circuito general: fase, neutro y tierra. */
  conductores: 3
}

const centroide = pts => {
  if (!pts?.length) return { x: 0, y: 0 }
  const s = pts.reduce((a, p) => ({ x: a.x + p.x, y: a.y + p.y }), { x: 0, y: 0 })
  return { x: s.x / pts.length, y: s.y / pts.length }
}
const r1 = n => Math.round(n * 10) / 10

/**
 * Trayecto del tablero a un recinto, por plafón.
 * @returns { metrosPlafon, supuesto, plantasDistintas, desde }
 */
export function trayectoAlRecinto(plano, recintoId, alturas = ALTURAS_BASE) {
  const a = { ...ALTURAS_BASE, ...alturas }
  const recinto = plano?.recintos?.[recintoId]
  const tablero = plano?.servicios?.tablero
  if (!recinto?.poligono?.length) return { metrosPlafon: a.trayectoPorDefecto, supuesto: true, plantasDistintas: false, desde: 'sin recinto' }
  if (!tablero) return { metrosPlafon: a.trayectoPorDefecto, supuesto: true, plantasDistintas: false, desde: 'tablero sin ubicar' }
  const c = centroide(recinto.poligono)
  const plantasDistintas = (tablero.planta || 'pb') !== (recinto.planta || 'pb')
  const horizontal = distOrtogonal({ x: tablero.x, y: tablero.y }, c)
  return {
    metrosPlafon: r1(horizontal + (plantasDistintas ? a.entrepiso : 0)),
    supuesto: false, plantasDistintas, desde: 'tablero'
  }
}

/** Cuántas bajadas hay en el recinto y de qué alto — solo para EXPLICAR; el
    material de cada bajada ya viene en la receta de cada salida. */
export function bajadasDe(objetosDelRecinto, alturas = ALTURAS_BASE) {
  const a = { ...ALTURAS_BASE, ...alturas }
  let contactos = 0, apagadores = 0
  for (const o of objetosDelRecinto) {
    if (o.estado !== 'propuesto') continue
    if (o.tipo === 'contacto' || o.tipo === 'contacto-agua') contactos += o.q || 0
    if (o.tipo === 'salida-luz') apagadores += o.q || 0
    if (o.tipo === 'escalera') apagadores += 2 * (o.q || 0)
    if (o.tipo === 'aparato' && o.sku === 'CTO-DED') contactos += o.q || 0
  }
  return {
    contactos, apagadores,
    metros: r1(contactos * (a.entrepiso - a.contacto) + apagadores * (a.entrepiso - a.apagador))
  }
}

/**
 * Lo que se cobra como ALIMENTACIÓN del recinto y cómo se explica.
 * @returns { metrosPlafon, metrosCable, metrosTubo, supuesto, bajadas, explicacion, supuestos[] }
 */
export function cableDelRecinto(plano, recintoId, objetosDelRecinto, alturas = ALTURAS_BASE) {
  const a = { ...ALTURAS_BASE, ...alturas }
  const t = trayectoAlRecinto(plano, recintoId, a)
  const b = bajadasDe(objetosDelRecinto, a)
  const nombre = plano?.recintos?.[recintoId]?.nombre || 'el recinto'
  const metrosTubo = r1(t.metrosPlafon * (1 + a.holgura))
  const metrosCable = r1(metrosTubo * a.conductores)
  const partes = [
    t.supuesto
      ? `Del tablero a ${nombre} se supusieron ${t.metrosPlafon} m por plafón (el tablero no está ubicado en el plano)`
      : `Del tablero a ${nombre} son ${t.metrosPlafon} m por plafón${t.plantasDistintas ? ' (incluye el cambio de planta)' : ''}`
  ]
  if (b.contactos || b.apagadores) {
    const trozos = []
    if (b.contactos) trozos.push(`${b.contactos} ${b.contactos === 1 ? 'bajada' : 'bajadas'} a contacto a ${a.contacto} m`)
    if (b.apagadores) trozos.push(`${b.apagadores} ${b.apagadores === 1 ? 'bajada' : 'bajadas'} a apagador a ${a.apagador} m`)
    partes.push(`${trozos.join(' y ')} (su cable va en cada salida)`)
  }
  partes.push(`alimentación: ${metrosTubo} m de tubo y ${metrosCable} m de cable (${a.conductores} conductores con ${Math.round(a.holgura * 100)} % de holgura)`)
  const supuestos = []
  if (t.supuesto) supuestos.push(`La alimentación a ${nombre} se calculó con ${t.metrosPlafon} m supuestos: ubica el tablero en el plano para medirla.`)
  if (a.sinVerificar) supuestos.push('Alturas de salida (0.4 m contacto, 1.2 m apagador) y holgura del 10 % son práctica de obra, pendientes de fijar con fuente.')
  return { metrosPlafon: t.metrosPlafon, metrosCable, metrosTubo, supuesto: t.supuesto, bajadas: b, explicacion: partes.join('; ') + '.', supuestos }
}
